<template>
    <div class="">
        <!-- DataTable Component -->
        <DataTable
            :perPage="itemsPerPage"
            :columns="visibleColumns"
            :items="tableData"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :selectedItems="selectedItems"
            :enableDragDrop="false"
            :enableMultiSelect="false"
            rowClass="text-center"
            :sortBy="sortColumn"
            :sortOrder="sortDirection"
            @page-changed="handlePageChange"
            @item-selected="toggleItemSelection"
            @select-all-toggled="toggleSelectAll"
            @sort="handleSort"
        >
            <!-- Toolbar Slot -->
            <template #toolbar>
                <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                    <!-- Search Input -->
                    <div class="ppv2-relative">
                        <Search v-model="search" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                    </div>

                    <div class="ppv2-flex-1"></div>
                    <!-- Documentation Button -->
                    <a
                        href="/back-office/v3/applications/documentation"
                        class="ppv2-min-h-[40px] ppv2-text-black ppv2-px-4 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-black ppv2-border-opacity-20 hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-transition-all ppv2-shadow-lg"
                    >
                        <span
                            class="ppv2-flex ppv2-items-center ppv2-gap-3 poppinsMedium ppv2-text-black"
                            :style="{ fontSize: normalizeFontSize(14) }"
                        >
                            <FileText :size="20" color="currentColor" />
                            {{ $t('api.view_documentation') }}
                        </span>
                    </a>
                    <!-- Create Button -->
                    <BaseButton
                        variant="purple"
                        :custom-height="40"
                        :custom-font-size="14"
                        custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                        @click="handleCreate"
                    >
                        <Plus :size="17" />
                        <span class="poppinsRegular">
                            {{ $t('api.create') }}
                        </span>
                    </BaseButton>
                </div>
            </template>

            <!-- Column: Name -->
            <template #column-name="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.name }}
                </span>
            </template>

            <!-- Column: ID -->
            <template #column-id="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.id }}
                </span>
            </template>

            <!-- Column: Actions -->
            <template #column-actions="{ item }">
                <div class="ppv2-relative ppv2-flex ppv2-items-center ppv2-justify-center">
                    <button
                        @click.stop="toggleActionMenu(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :ref="`actionButton-${item.id}`"
                    >
                        <MoreVertical :size="18" />
                    </button>

                    <!-- Actions Dropdown Menu -->
                    <div
                        v-if="activeActionMenu === item.id"
                        class="actionsDropdown ppv2-fixed ppv2-rounded-xl ppv2-overflow-hidden ppv2-z-[100] ppv2-min-w-[200px] ppv2-shadow-lg"
                        :style="dropdownPosition"
                        :ref="`actionMenu-${item.id}`"
                    >
                        <!-- Modifier -->
                        <div
                            @click="handleEdit(item)"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-transition-colors ppv2-cursor-pointer"
                        >
                            <FilePen :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('common.edit')
                            }}</span>
                        </div>

                        <div
                            @click="handleShow(item)"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-transition-colors ppv2-cursor-pointer"
                        >
                            <Eye :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('common.show')
                            }}</span>
                        </div>

                        <!-- Supprimer -->
                        <div
                            @click="deleteSingleItem(item.id)"
                            class="actionItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-transition-colors ppv2-cursor-pointer"
                        >
                            <Trash2 :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('common.delete')
                            }}</span>
                        </div>
                    </div>
                </div>
            </template>

            <template #bulk-actions="{ selectedCount }">
                <span :style="{ fontSize: normalizeFontSize(15) }" class="poppinsRegular ppv2-text-black">
                    {{ $t('applications.selected_items', { count: selectedCount }) }}
                </span>
                <span class="poppinsRegular ppv2-text-white ppv2-text-opacity-50" :style="{ fontSize: normalizeFontSize(15) }"
                    >|</span
                >
                <BaseButton
                    variant="outline"
                    @click="deleteSelectedItems"
                    customClass="!ppv2-px-4 !ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                >
                    <Trash2 :size="14" />
                    <span>{{ $t('common.deleteSelection') }}</span>
                </BaseButton>
            </template>
        </DataTable>

        <!-- Application Form Popup -->
        <ApplicationForm
            v-model="showFormPopup"
            :applicationData="editingItem"
            @success="handleFormSuccess"
            @close="showFormPopup = false"
            :token="token"
        />

        <!-- Application View Popup -->
        <CustomDialog v-model="showViewPopup" :defaultTitle="$t('api.view.title')" width="600px">
            <template #content>
                <div class="ppv2-flex ppv2-flex-col ppv2-gap-6 ppv2-py-4" v-if="viewItem">
                    <!-- Application ID -->
                    <div class="ppv2-flex ppv2-flex-col ppv2-gap-2">
                        <label class="poppinsMedium ppv2-text-[#4A5568]" :style="{ fontSize: normalizeFontSize(14) }">
                            {{ $t('api.view.identifiant') }}
                        </label>
                        <div
                            class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-bg-[#F7FAFC] ppv2-p-3 ppv2-rounded-xl ppv2-border ppv2-border-[#E2E8F0]"
                        >
                            <span class="poppinsRegular ppv2-text-black ppv2-break-all">{{ viewItem.id }}</span>
                            <button @click="copyToClipboard(viewItem.id)" class="ppv2-text-[#7D2DBD] hover:ppv2-opacity-70">
                                <Copy :size="18" />
                            </button>
                        </div>
                    </div>

                    <!-- Application Secret -->
                    <div class="ppv2-flex ppv2-flex-col ppv2-gap-2">
                        <label class="poppinsMedium ppv2-text-[#4A5568]" :style="{ fontSize: normalizeFontSize(14) }">
                            {{ $t('api.view.secret') }}
                        </label>
                        <div
                            class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-bg-[#F7FAFC] ppv2-p-3 ppv2-rounded-xl ppv2-border ppv2-border-[#E2E8F0]"
                        >
                            <span class="poppinsRegular ppv2-text-black ppv2-break-all">{{ viewItem.secret }}</span>
                            <button @click="copyToClipboard(viewItem.secret)" class="ppv2-text-[#7D2DBD] hover:ppv2-opacity-70">
                                <Copy :size="18" />
                            </button>
                        </div>
                    </div>
                </div>
            </template>
            <template #actions>
                <BaseButton variant="outline" @click="showViewPopup = false" :customHeight="48" customClass="!ppv2-min-w-[160px]">
                    {{ $t('codeModal.close') }}
                </BaseButton>
            </template>
        </CustomDialog>

        <!-- Delete Confirmation Popup -->
        <CustomDialog
            v-model="showDeletePopup"
            :defaultTitle="$t('applications.delete_title')"
            customClassTitle="poppinsSemiBold"
            noScroll
        >
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ deletePopupDescription }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    variant="outline"
                    @click="showDeletePopup = false"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    variant="danger"
                    @click="confirmDelete"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import { FilePen, Trash2, Plus, MoreVertical, CheckCircle, XCircle, Eye, Copy, FileText } from 'lucide-vue'
import DataTable from '../../Shared/DataTable.vue'
import Search from '../Experience/components/Search.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import normalizeSize from '../../../utils/normalizeSize'
import ApplicationForm from './ApplicationForm.vue'
import axios from 'axios'
import { eventBus } from '../../Customer/eventBus'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../utils/perPage'

export default {
    name: 'ApplicationsTable',
    components: {
        FilePen,
        Trash2,
        Plus,
        MoreVertical,
        CheckCircle,
        XCircle,
        DataTable,
        Search,
        BaseButton,
        CustomDialog,
        ApplicationForm,
        Eye,
        Copy,
        FileText,
    },
    props: {
        userId: {
            type: [Number, String, null],
            default: null,
        },
        token: { type: String, default: '' },
        initialId: { type: String, default: '' },
    },
    data() {
        return {
            search: '',
            tableData: [],
            itemsPerPage: getPerPage('applications_items_per_page', 10),
            recordsTotal: 0,
            currentPage: 1,
            loading: false,
            selectedItems: [],
            selectAll: false,
            showDeletePopup: false,
            showFormPopup: false,
            editingItem: null,
            itemToDelete: null,
            activeActionMenu: null,
            dropdownPosition: { top: '0px', left: '0px' },
            sortColumn: 'id',
            sortDirection: 'desc',
            selectedColumnKeys: ['id', 'name', 'actions'],
            deletePopupDescription: '',
            showViewPopup: false,
            viewItem: null,
        }
    },
    computed: {
        allColumns() {
            return [
                { key: 'id', label: this.$t('applications.column_id'), sortable: true },
                { key: 'name', label: this.$t('applications.column_name'), sortable: true },
                { key: 'actions', label: this.$t('common.actions'), sortable: false },
            ]
        },
        totalPages() {
            return Math.ceil(this.recordsTotal / this.itemsPerPage)
        },
        visibleColumns() {
            return this.allColumns.filter((column) => this.selectedColumnKeys.includes(column.key))
        },
    },
    mounted() {
        this.fetchData()
        if (this.initialId) {
            this.fetchInitialItem()
        }
        window.addEventListener('scroll', this.updateDropdownPosition)
        window.addEventListener('resize', this.updateDropdownPosition)
        document.addEventListener('click', this.handleClickOutside)
        eventBus.$on('dt-selected-perpage-changed', this.handlePerPageChange)
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.updateDropdownPosition)
        window.removeEventListener('resize', this.updateDropdownPosition)
        document.removeEventListener('click', this.handleClickOutside)
        eventBus.$off('dt-selected-perpage-changed', this.handlePerPageChange)
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        handleSearchUpdate(searchValue) {
            this.search = searchValue
            this.currentPage = 1
            this.fetchData()
        },
        handleSort(key) {
            if (this.sortColumn === key) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortColumn = key
                this.sortDirection = 'asc'
            }
            this.fetchData()
        },
        handleClickOutside(event) {
            if (this.activeActionMenu) {
                const actionButton = this.$refs[`actionButton-${this.activeActionMenu}`]
                const actionMenu = this.$refs[`actionMenu-${this.activeActionMenu}`]
                const btnEl = Array.isArray(actionButton) ? actionButton[0] : actionButton
                const menuEl = Array.isArray(actionMenu) ? actionMenu[0] : actionMenu
                if (!btnEl?.contains(event.target) && !menuEl?.contains(event.target)) {
                    this.activeActionMenu = null
                }
            }
        },
        toggleActionMenu(itemId) {
            if (this.activeActionMenu === itemId) {
                this.activeActionMenu = null
            } else {
                this.activeActionMenu = itemId
                this.$nextTick(() => {
                    this.calculateDropdownPosition(itemId)
                })
            }
        },
        calculateDropdownPosition(itemId) {
            const button = this.$refs[`actionButton-${itemId}`]
            const btnEl = Array.isArray(button) ? button[0] : button
            if (!btnEl) return
            const rect = btnEl.getBoundingClientRect()
            this.dropdownPosition = {
                top: `${rect.bottom + 4}px`,
                left: `${rect.right - 200}px`,
            }
        },
        updateDropdownPosition() {
            if (this.activeActionMenu) {
                this.calculateDropdownPosition(this.activeActionMenu)
            }
        },
        async fetchData() {
            this.loading = true
            try {
                const response = await axios.get('/api/v3/applications/list', {
                    params: {
                        currentPage: this.currentPage,
                        itemsPerPage: this.itemsPerPage,
                        search: this.search,
                        orderBy: this.sortColumn,
                        orderDirection: this.sortDirection,
                    },
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                })

                if (response.data.success) {
                    this.tableData = response.data.data
                    this.recordsTotal = response.data.total
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.loading = false
            }
        },
        async fetchInitialItem() {
            try {
                const response = await axios.get(`/api/v3/applications/show/${this.initialId}`, {
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                })
                if (response.data.success) {
                    this.editingItem = response.data.data
                    this.showFormPopup = true
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error fetching initial application:', error)
            }
        },
        toggleItemSelection(itemId) {
            const index = this.selectedItems.indexOf(itemId)
            if (index > -1) {
                this.selectedItems.splice(index, 1)
            } else {
                this.selectedItems.push(itemId)
            }
            this.selectAll = this.selectedItems.length === this.tableData.length
        },

        toggleSelectAll() {
            if (this.selectAll) {
                this.selectedItems = this.tableData.map((item) => item.id)
            } else {
                this.selectedItems = []
            }
        },
        handleCreate() {
            this.editingItem = null
            this.showFormPopup = true
        },
        handleEdit(item) {
            this.activeActionMenu = null
            this.editingItem = item
            this.showFormPopup = true
        },
        handleFormSuccess() {
            this.fetchData()
        },
        deleteSingleItem(itemId) {
            this.deletePopupDescription = this.$t('applications.delete_confirm_single')
            this.activeActionMenu = null
            this.itemToDelete = itemId
            this.showDeletePopup = true
        },
        async confirmDelete() {
            try {
                await axios.delete(`/api/v3/applications/delete/${this.itemToDelete}`, {
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                })
                this.$toast.success(this.$t('applications.delete_success'))
                this.fetchData()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error deleting application:', error)
                this.$toast.error(this.$t('common.delete_error'))
            } finally {
                this.showDeletePopup = false
                this.itemToDelete = null
            }
        },
        async deleteSelectedItems() {
            if (this.selectedItems.length === 0) return

            this.deleteAction = 'multiple'
            this.deletePopupDescription = this.$t('products.deletePopup.message')
            this.showDeletePopup = true
        },
        handlePerPageChange(perPage) {
            this.itemsPerPage = Number(perPage)
            setPerPage('applications_items_per_page', this.itemsPerPage)
            this.fetchData()
        },
        async handleShow(item) {
            this.activeActionMenu = null
            try {
                const response = await axios.get(`/api/v3/applications/show/${item.id}`, {
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                })
                if (response.data.success) {
                    this.viewItem = response.data.data
                    this.showViewPopup = true
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error fetching application details:', error)
                this.$toast.error(this.$t('common.load_error'))
            }
        },
        copyToClipboard(text) {
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard
                    .writeText(text)
                    .then(() => {
                        this.$toast.success(this.$t('common.copied_clipboard'))
                    })
                    .catch(() => {
                        this.fallbackCopyTextToClipboard(text)
                    })
            } else {
                this.fallbackCopyTextToClipboard(text)
            }
        },
        fallbackCopyTextToClipboard(text) {
            const textArea = document.createElement('textarea')
            textArea.value = text
            textArea.style.position = 'fixed'
            textArea.style.left = '-999999px'
            textArea.style.top = '-999999px'
            document.body.appendChild(textArea)
            textArea.focus()
            textArea.select()
            try {
                document.execCommand('copy')
                this.$toast.success(this.$t('common.copied_clipboard'))
            } catch (err) {
                this.$toast.error(this.$t('common.copy_error'))
            }
            document.body.removeChild(textArea)
        },
    },
    watch: {
        showFormPopup(val) {
            if (!val) {
                this.editingItem = null
            }
        },
        selectedItems: {
            handler(newSelection) {
                const currentPageIds = this.tableData.map((item) => item.id)
                this.selectAll = currentPageIds.length > 0 && currentPageIds.every((id) => newSelection.includes(id))
            },
            deep: true,
        },
    },
}
</script>

<style scoped lang="scss">
.actionsDropdown {
    background: white;
    border: 1px solid #e2e8f0;

    .actionItem {
        color: #4a5568;
        &:hover {
            background: #f7fafc;
            color: #7d2dbd;
        }
    }
}
</style>
